import { useEffect } from "react";
import { useParams, Link, Navigate, useNavigate } from "react-router-dom";
import { ChevronRight, ArrowUpRight, MessageCircle, CheckCircle2,
    Thermometer, Sun, CloudRainWind, Zap, Cpu, SunMedium, Wind, Droplets, ShieldCheck, Waves, Accessibility, Sparkles, Leaf, Gem, DraftingCompass } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { VideoPlayer } from "@/components/VideoPlayer";
import { findConcept, CONCEPT_PAGES } from "@/data/concepts";
import { CONTACT } from "@/data/content";
import { trackEvent, EVENTS } from "@/lib/analytics";

const ICONS = { Thermometer, Sun, CloudRainWind, Zap, Cpu, SunMedium, Wind, Droplets, ShieldCheck, Waves, Accessibility, Sparkles, Leaf, Gem, DraftingCompass };

const BLOCKS = [
    { key: "what", title: "What is it?" },
    { key: "why", title: "Why does it matter?" },
    { key: "how", title: "How does it work?" },
];

export default function ConceptDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const concept = findConcept(slug);

    useEffect(() => { window.scrollTo(0, 0); }, [slug]);

    if (!concept) return <Navigate to="/" replace />;
    const Icon = ICONS[concept.icon] || Sparkles;
    const related = concept.related.map(findConcept).filter(Boolean);

    return (
        <div className="bg-omsivo-paper">
            <Navbar />
            <section className="blueprint-fine noise relative overflow-hidden pb-20 pt-28 lg:pt-36" data-testid="concept-hero">
                <div className="relative z-10 mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10">
                    <nav className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/45" aria-label="Breadcrumb" data-testid="concept-breadcrumb">
                        <Link to="/" className="transition-colors hover:text-white">Home</Link>
                        <ChevronRight className="h-3 w-3" />
                        <Link to="/#concepts" className="transition-colors hover:text-white">Concepts</Link>
                        <ChevronRight className="h-3 w-3" />
                        <span className="text-flame">{concept.name}</span>
                    </nav>
                    <div className="mt-10 grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
                        <div className="lg:col-span-7">
                            <Reveal y={16}>
                                <span className="inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/5 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.26em] text-white/65 backdrop-blur">
                                    <Icon className="h-4 w-4 text-flame" /> OMSIVO Concept
                                </span>
                            </Reveal>
                            <MaskedLines
                                className="mt-6 font-display font-black uppercase leading-[1.0] tracking-tight"
                                lineClassName="text-4xl text-white sm:text-5xl lg:text-6xl"
                                lines={[concept.name]}
                            />
                            <Reveal delay={0.4}>
                                <p className="mt-6 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">{concept.tagline}</p>
                            </Reveal>
                            <Reveal delay={0.5}>
                                <div className="mt-9 flex flex-wrap items-center gap-4">
                                    <button onClick={() => { trackEvent(EVENTS.estimateClick, { location: `concept:${slug}` }); navigate("/", { state: { scrollTo: "#contact" } }); }}
                                        data-testid="concept-discuss-btn"
                                        className="rounded-full bg-flame px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.1em] text-white shadow-xl shadow-flame/25 transition-transform hover:-translate-y-0.5">
                                        Discuss This Concept
                                    </button>
                                    <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="concept-whatsapp-btn"
                                        onClick={() => trackEvent(EVENTS.whatsappClick, { location: `concept:${slug}` })}
                                        className="flex items-center gap-2 rounded-full border border-white/25 px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.1em] text-white transition-colors hover:border-flame hover:text-flame">
                                        <MessageCircle className="h-4 w-4 text-flame" /> WhatsApp
                                    </a>
                                </div>
                            </Reveal>
                        </div>
                        <Reveal className="lg:col-span-5" delay={0.2}>
                            <div className="spotlight-frame overflow-hidden rounded-[28px] border border-white/10 shadow-2xl shadow-black/50"
                                style={{ clipPath: "polygon(0 0, 100% 0, 100% 90%, 90% 100%, 0 100%)" }}>
                                <img src={concept.image} alt={`${concept.name} — concept visualization`} className="h-[300px] w-full object-cover lg:h-[380px]" data-testid="concept-hero-image" />
                            </div>
                            <p className="mt-3 text-right font-mono text-[8px] uppercase tracking-[0.22em] text-white/35">Concept visualization</p>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="py-20 lg:py-28" data-testid="concept-details">
                <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10">
                    <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
                        <div className="space-y-4 lg:col-span-7">
                            {BLOCKS.map((b, i) => (
                                <Reveal key={b.key} delay={0.06 * i}>
                                    <div className="rounded-2xl border border-omsivo-navy/10 bg-white p-7" data-testid={`concept-block-${b.key}`}>
                                        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.24em] text-flame-dark">{String(i + 1).padStart(2, "0")}</p>
                                        <h2 className="mt-2 font-display text-xl font-extrabold uppercase tracking-wide text-omsivo-navy">{b.title}</h2>
                                        <p className="mt-3 text-[15px] leading-relaxed text-slate-700">{concept[b.key]}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                        <div className="lg:col-span-5">
                            <Reveal>
                                <div className="rounded-2xl border border-omsivo-navy/10 bg-omsivo-navy p-7" data-testid="concept-considerations">
                                    <h2 className="font-display text-lg font-extrabold uppercase tracking-wide text-white">Key design considerations</h2>
                                    <div className="mt-5 space-y-3">
                                        {concept.considerations.map((c) => (
                                            <div key={c} className="flex items-start gap-3">
                                                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-flame" />
                                                <p className="text-sm leading-relaxed text-white/80">{c}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </Reveal>
                            <Reveal delay={0.1}>
                                <div className="mt-8" data-testid="concept-video">
                                    <h2 className="font-display text-lg font-extrabold uppercase tracking-wide text-omsivo-navy">Watch the concept</h2>
                                    <VideoPlayer className="mt-5 rounded-2xl border border-omsivo-navy/10" src={null} label={`${concept.name} concept video`} testid="concept-video-player" />
                                    <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.2em] text-slate-400">Concept video coming soon</p>
                                </div>
                            </Reveal>
                        </div>
                    </div>

                    <div className="mt-20">
                        <Reveal><h2 className="font-display text-2xl font-extrabold uppercase tracking-wide text-omsivo-navy sm:text-3xl">Related concepts</h2></Reveal>
                        <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-3">
                            {related.map((r) => (
                                <Link key={r.slug} to={`/concepts/${r.slug}`} data-testid={`concept-related-${r.slug}`}
                                    className="lift-card group rounded-2xl border border-omsivo-navy/10 bg-white p-6">
                                    <div className="flex items-center justify-between">
                                        <h3 className="font-display text-base font-extrabold uppercase tracking-[0.05em] text-omsivo-navy">{r.name}</h3>
                                        <ArrowUpRight className="h-4 w-4 text-slate-300 transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-flame" />
                                    </div>
                                    <p className="mt-2 line-clamp-2 text-[13px] leading-relaxed text-slate-600">{r.tagline}</p>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
}

export { CONCEPT_PAGES };
